/**
 * Analytics — the business over time (ADR-044).
 *
 * Four readings, in the order an owner asks for them: how did the days go
 * (the time-series), do signups become depositors (the funnel), do players
 * come back (the cohorts), and who is the book really made of (the lifetime
 * P&L list). Every figure is derived server-side from the money tables at
 * read time; this screen invents nothing and can write nothing.
 *
 * The chart is a plain inline SVG — the panel has no charting dependency and
 * a time-series needs a line, an area and a hover title, not a library. All
 * geometry math uses JS numbers; every DISPLAYED amount stays the decimal
 * string it arrived as (`lib/money.ts`), because geometry can be approximate
 * and money cannot.
 */
import { useMemo, useState, type ReactNode } from 'react';
import {
  fetchCohorts,
  fetchDailySeries,
  fetchFunnel,
  fetchPlayerLifetime,
} from '../api/client.ts';
import type { DailyMetric, DepositFunnel, PlayerLifetime, RetentionCohort } from '../api/types.ts';
import { useAsync, type AsyncState } from '../lib/useAsync.ts';
import { age, absolute } from '../lib/datetime.ts';
import { format, formatCompact, sum, toUnits } from '../lib/money.ts';
import {
  Card,
  EmptyState,
  ErrorState,
  Money,
  Stat,
  StatusBadge,
  TableSkeleton,
  UsageMeter,
} from '../ui/primitives.tsx';
import { IconInbound, IconPeople, IconTrend } from '../ui/icons.tsx';

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
] as const;

type Metric = 'turnover' | 'grossGamingRevenue' | 'depositsTotal' | 'activePlayers';

const METRICS: { id: Metric; label: string; money: boolean }[] = [
  { id: 'turnover', label: 'Turnover', money: true },
  { id: 'grossGamingRevenue', label: 'GGR', money: true },
  { id: 'depositsTotal', label: 'Deposits', money: true },
  { id: 'activePlayers', label: 'Actives', money: false },
];

export function Analytics(): ReactNode {
  const [days, setDays] = useState<number>(30);
  const [metric, setMetric] = useState<Metric>('turnover');

  const series = useAsync(() => fetchDailySeries(days), [days]);
  const funnel = useAsync(() => fetchFunnel(days), [days]);
  const cohorts = useAsync(() => fetchCohorts(8), []);
  const whales = useAsync(() => fetchPlayerLifetime(50), []);

  const rows = series.data?.days ?? [];
  const totals = useMemo(
    () => ({
      turnover: sum(rows.map((r) => r.turnover)),
      ggr: sum(rows.map((r) => r.grossGamingRevenue)),
      deposits: sum(rows.map((r) => r.depositsTotal)),
      withdrawals: sum(rows.map((r) => r.withdrawalsTotal)),
      newPlayers: rows.reduce((n, r) => n + r.newPlayers, 0),
      bets: rows.reduce((n, r) => n + r.betCount, 0),
    }),
    [rows],
  );

  return (
    <div className="stack">
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Reporting range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={`segment${days === r.days ? ' is-active' : ''}`}
              aria-pressed={days === r.days}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <span className="card__spacer" />
        {series.data ? (
          <span className="field__hint num">
            {series.data.from} → {series.data.to} · UTC days
          </span>
        ) : null}
      </div>

      <div className="stats">
        <Stat
          icon={<IconTrend size="sm" />}
          label="Turnover"
          value={rows.length ? formatCompact(totals.turnover) : '—'}
          note={rows.length ? `${totals.bets.toLocaleString()} bets` : ''}
          title={rows.length ? format(totals.turnover, { places: 4 }) : ''}
        />
        <Stat
          label="Gross gaming revenue"
          value={rows.length ? formatCompact(totals.ggr) : '—'}
          tone={rows.length && toUnits(totals.ggr) < 0n ? 'loss' : 'win'}
          note="turnover less payouts"
          title={rows.length ? format(totals.ggr, { places: 4 }) : ''}
        />
        <Stat
          icon={<IconInbound size="sm" />}
          label="Deposits in"
          value={rows.length ? formatCompact(totals.deposits) : '—'}
          note={rows.length ? <>out {formatCompact(totals.withdrawals)}</> : ''}
          title={rows.length ? format(totals.deposits, { places: 4 }) : ''}
        />
        <Stat
          icon={<IconPeople size="sm" />}
          label="New players"
          value={rows.length ? totals.newPlayers.toLocaleString() : '—'}
          note={`over ${days} days`}
        />
      </div>

      <Card
        feature
        title="Day by day"
        sub="One point per UTC day, derived from the money tables at read time"
        icon={<IconTrend />}
        actions={
          <div className="segmented" role="group" aria-label="Chart metric">
            {METRICS.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`segment${metric === m.id ? ' is-active' : ''}`}
                aria-pressed={metric === m.id}
                onClick={() => setMetric(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
        }
      >
        {series.error ? (
          <ErrorState title="The series did not load" detail={series.error} onRetry={series.reload} />
        ) : rows.length === 0 ? (
          <EmptyState
            compact
            icon={<IconTrend size="lg" />}
            title={series.loading ? 'Loading the series…' : 'No days in range'}
            hint="Figures appear as soon as a day has any settled activity."
          />
        ) : (
          <SeriesChart rows={rows} metric={metric} />
        )}
      </Card>

      <div className="split">
        <FunnelCard funnel={funnel} days={days} />
        <CohortCard cohorts={cohorts} />
      </div>

      <WhaleCard whales={whales} />
    </div>
  );
}

// -------------------------------------------------------------------- chart

/**
 * Plain SVG: an area, a line, and one invisible hover band per day carrying a
 * `<title>` with the exact figure — the honest amount lives in the tooltip
 * and the table-minded can read the whale list below. Geometry only here.
 */
function SeriesChart({ rows, metric }: { rows: DailyMetric[]; metric: Metric }): ReactNode {
  const W = 720;
  const H = 200;
  const PAD = { top: 12, right: 8, bottom: 22, left: 8 };
  const metricDef = METRICS.find((m) => m.id === metric)!;

  const values = rows.map((r) => Number(r[metric]));
  const max = Math.max(...values, 1);
  const peakRow = rows[values.indexOf(Math.max(...values))] ?? rows[0]!;
  const min = Math.min(...values, 0);
  const span = max - min || 1;

  const x = (i: number): number =>
    rows.length === 1
      ? W / 2
      : PAD.left + (i * (W - PAD.left - PAD.right)) / (rows.length - 1);
  const y = (v: number): number =>
    H - PAD.bottom - ((v - min) * (H - PAD.top - PAD.bottom)) / span;

  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(rows.length - 1).toFixed(1)},${H - PAD.bottom} L${x(0).toFixed(1)},${H - PAD.bottom} Z`;
  const zero = y(0);

  const fmt = (r: DailyMetric): string =>
    metricDef.money
      ? format(r[metric] as string, { places: 2 })
      : Number(r[metric]).toLocaleString();

  // Sparse day labels: first, last, and roughly every sixth.
  const labelEvery = Math.max(1, Math.ceil(rows.length / 6));

  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={`${metricDef.label} per day`}
        style={{ display: 'block' }}
      >
        {/* Baseline (zero when GGR dips negative, else the floor). */}
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={min < 0 ? zero : H - PAD.bottom}
          y2={min < 0 ? zero : H - PAD.bottom}
          stroke="var(--border-default)"
          strokeWidth="1"
        />
        <path d={area} fill="var(--accent-subtle)" stroke="none" />
        <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" />
        {rows.map((r, i) => (
          <g key={r.day}>
            <circle cx={x(i)} cy={y(values[i]!)} r="2.5" fill="var(--accent)" />
            {/* Full-height hover band with the exact figure. */}
            <rect
              x={x(i) - (W / rows.length) / 2}
              y={0}
              width={W / rows.length}
              height={H - PAD.bottom}
              fill="transparent"
            >
              <title>{`${r.day} — ${metricDef.label}: ${fmt(r)}`}</title>
            </rect>
            {i % labelEvery === 0 || i === rows.length - 1 ? (
              <text
                x={x(i)}
                y={H - 6}
                textAnchor="middle"
                fontSize="10"
                fill="var(--text-muted)"
              >
                {r.day.slice(5)}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
      <figcaption className="field__hint">
        Peak {metricDef.money ? formatCompact(peakRow[metric] as string) : Number(peakRow[metric]).toLocaleString()}{' '}
        on {peakRow.day} · hover a day for the exact figure
      </figcaption>
    </figure>
  );
}

// ------------------------------------------------------------------- funnel

function FunnelCard({
  funnel,
  days,
}: {
  funnel: AsyncState<DepositFunnel>;
  days: number;
}): ReactNode {
  const data = funnel.data;
  const steps = data
    ? [
        { label: 'Registered', count: data.registered, pct: '100.00' },
        { label: 'Made a deposit', count: data.firstDeposited, pct: data.depositConversionPct },
        { label: 'Placed a bet', count: data.firstBet, pct: data.betConversionPct },
      ]
    : [];

  return (
    <Card
      title="Deposit funnel"
      sub={`Players who signed up in the last ${days} days, wherever their deposit or bet landed`}
      icon={<IconPeople />}
    >
      {funnel.error ? (
        <ErrorState title="The funnel did not load" detail={funnel.error} onRetry={funnel.reload} />
      ) : !data ? (
        <TableSkeleton columns={['text', 'num', 'num']} rows={3} />
      ) : data.registered === 0 ? (
        <EmptyState
          compact
          icon={<IconPeople size="lg" />}
          title="Nobody signed up in this range"
          hint="The funnel reads only players whose account was created inside the window."
        />
      ) : (
        <div className="stack">
          {steps.map((step) => (
            <div key={step.label}>
              <div className="toolbar">
                <span>{step.label}</span>
                <span className="card__spacer" />
                <span className="mono">{step.count.toLocaleString()}</span>
              </div>
              <UsageMeter percentage={step.pct} level="ok" />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ cohorts

function CohortCard({
  cohorts,
}: {
  cohorts: AsyncState<RetentionCohort[]>;
}): ReactNode {
  const rows = cohorts.data ?? [];
  const maxWeeks = rows.reduce((n, c) => Math.max(n, c.active.length), 0);

  return (
    <Card
      title="Retention cohorts"
      sub="Weekly signup cohorts; a cell is the share who placed a bet that week"
      icon={<IconPeople />}
      flush
    >
      {cohorts.error ? (
        <ErrorState title="Cohorts did not load" detail={cohorts.error} onRetry={cohorts.reload} />
      ) : cohorts.loading && rows.length === 0 ? (
        <TableSkeleton columns={['text', 'num', 'num', 'num', 'num']} rows={6} />
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          icon={<IconPeople size="lg" />}
          title="No signups in the last eight weeks"
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Cohort week</th>
                <th className="right">Size</th>
                {Array.from({ length: maxWeeks }, (_, w) => (
                  <th key={w} className="right">
                    W{w}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.cohortWeek}>
                  <td className="mono nowrap">{c.cohortWeek}</td>
                  <td className="right mono">{c.size.toLocaleString()}</td>
                  {Array.from({ length: maxWeeks }, (_, w) => {
                    const active = c.active[w];
                    if (active === undefined) return <td key={w} className="right dim">·</td>;
                    const share = c.size === 0 ? 0 : active / c.size;
                    return (
                      <td
                        key={w}
                        className="right mono"
                        title={`${active.toLocaleString()} of ${c.size.toLocaleString()} bet in week ${w}`}
                        style={{
                          background: `color-mix(in srgb, var(--accent) ${Math.round(share * 55)}%, transparent)`,
                        }}
                      >
                        {c.size === 0 ? '—' : `${Math.round(share * 100)}%`}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------- whales

function WhaleCard({
  whales,
}: {
  whales: AsyncState<PlayerLifetime[]>;
}): ReactNode {
  const rows = whales.data ?? [];
  return (
    <Card
      title="Lifetime P&L — the whale list"
      sub="Top players by lifetime house win. One row per player and currency; figures never mix units"
      icon={<IconPeople />}
      count={rows.length}
      flush
    >
      {whales.error ? (
        <ErrorState title="The list did not load" detail={whales.error} onRetry={whales.reload} />
      ) : whales.loading && rows.length === 0 ? (
        <TableSkeleton columns={['text', 'text', 'text', 'num', 'num', 'num', 'num', 'num']} rows={8} />
      ) : rows.length === 0 ? (
        <EmptyState compact icon={<IconPeople size="lg" />} title="No settled bets yet" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Status</th>
                <th>Currency</th>
                <th className="right">Turnover</th>
                <th className="right">Paid out</th>
                <th className="right">House win</th>
                <th className="right">Bets</th>
                <th className="right">Last bet</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={`${p.userId}-${p.currency}`}>
                  <td className="mono dim">{p.userId}</td>
                  <td className="nowrap cell-lead">{p.username}</td>
                  <td>
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="mono dim">{p.currency}</td>
                  <td className="right mono dim">
                    <Money value={p.turnover} />
                  </td>
                  <td className="right mono dim">
                    <Money value={p.payouts} />
                  </td>
                  <td className="right mono">
                    <Money value={p.grossGamingRevenue} colour signed />
                  </td>
                  <td className="right mono dim">{p.betCount.toLocaleString()}</td>
                  <td className="right mono muted" title={absolute(p.lastBetAt)}>
                    {age(p.lastBetAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
